import { TurnkeySigner } from '@turnkey/ethers';
import { ethers } from 'ethers';

import { AssetTransferCreds, createClient, getRpcUrl } from './tokenTransfer';

/**
 * Testing operations: run a real investment end to end, then take the tokens back. Runs on
 * whichever chain this deployment is configured for — Fuji on dev, mainnet on prod.
 *
 *   1. Tester runs the normal investor flow up to "I have paid my wire".
 *   2. Admin whitelists the tester's wallet here.
 *   3. Admin completes the investment as usual — tokens arrive unlocked.
 *   4. Admin reverts the wallet here — every token goes back to the treasury.
 *
 * Whitelisting is what makes the revert possible. The TokenLockerModule refuses to move a
 * non-whitelisted wallet's locked tokens — forcedTransfer included, because it still runs the
 * module's transfer hook — while a whitelisted sender may move its locked tranches too. So a
 * wallet whitelisted late can still be reverted; whitelisting first just means the tokens are
 * never locked at all.
 *
 * Both operations are signed in the browser with the Asset Transfer credentials. Neither picks
 * a wallet by assumption: each reads the role it needs on-chain (module owner, token agent,
 * compliance or Manager owner) and signs with whichever of the treasury and relayer wallets
 * holds it.
 *
 * Ported from the tested CLI scripts whitelistInvestorMainnet.ts (whitelist) and
 * transferInvestorToTreasury.ts (its lock pre-flight), plus dome-backend's
 * scripts/whitelist/manageWhitelistAddress.ts for chains whose lock module predates
 * setWhitelistAddressByOwner.
 */

const TOKEN_ABI = [
  'function balanceOf(address account) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function compliance() view returns (address)',
  'function isAgent(address account) view returns (bool)',
  'function forcedTransfer(address _from, address _to, uint256 _amount) returns (bool)',
];

const COMPLIANCE_ABI = [
  'function owner() view returns (address)',
  'function getModules() view returns (address[])',
  'function callModuleFunction(bytes callData, address _module)',
];

const MANAGER_ABI = [
  'function owner() view returns (address)',
  'function callComplianceModuleFunction(address _compliance, bytes callData, address _module)',
];

const LOCKER_ABI = [
  'function name() view returns (string)',
  'function isWhitelisted(address _address) view returns (bool)',
  'function getTreasuryAddress() view returns (address)',
  'function getAvailableBalance(address _investor) view returns (uint256)',
  'function getLockedAmount(address _investor) view returns (uint256)',
  'function setWhitelistAddress(address _address, bool _status)',
  'function owner() view returns (address)',
  'function setWhitelistAddressByOwner(address _address, bool _status)',
];

export type TestingResult = {
  txHash: string;
  /** Human-readable token amount moved — set by the revert only. */
  tokens?: string;
};

export type TestWalletLockStatus = {
  /** False when the token has no TokenLockerModule — nothing is ever locked. */
  hasLocker: boolean;
  whitelisted: boolean;
  locked: string;
  available: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const provider = () => new ethers.JsonRpcProvider(getRpcUrl());

async function findLocker(
  tokenAddress: string,
  p: ethers.Provider,
): Promise<{ compliance: string; locker: ethers.Contract | null }> {
  const token = new ethers.Contract(tokenAddress, TOKEN_ABI, p);
  const compliance: string = await token.compliance();
  const modules: string[] = await new ethers.Contract(
    compliance,
    COMPLIANCE_ABI,
    p,
  ).getModules();
  for (const m of modules) {
    const c = new ethers.Contract(m, LOCKER_ABI, p);
    try {
      if ((await c.name()) === 'TokenLockerModule')
        return { compliance, locker: c };
    } catch {
      /* not a module with name() — keep looking */
    }
  }
  return { compliance, locker: null };
}

/** The panel's two Turnkey wallets, the ones this page can sign with. */
function candidates(creds: AssetTransferCreds): string[] {
  return [
    ethers.getAddress(creds.walletAddress),
    ethers.getAddress(creds.relayerAddress),
  ];
}

function signerFor(
  creds: AssetTransferCreds,
  address: string,
  p: ethers.Provider,
) {
  return new TurnkeySigner({
    client: createClient(creds),
    organizationId: creds.organizationId,
    signWith: address,
  }).connect(p);
}

const sameAddress = (a: string, b: string) =>
  a.toLowerCase() === b.toLowerCase();

// ─── Network ──────────────────────────────────────────────────────────────────

export type ConnectedNetwork = {
  chainId: number;
  name: string;
  isMainnet: boolean;
};

/**
 * The chain this deployment's RPC actually answers for — read live rather than inferred from
 * the hostname, so the banner cannot claim testnet while the RPC points at mainnet.
 */
export async function getConnectedNetwork(): Promise<ConnectedNetwork> {
  const chainId = Number((await provider().getNetwork()).chainId);
  if (chainId === 43114)
    return { chainId, name: 'Avalanche Mainnet', isMainnet: true };
  if (chainId === 43113)
    return { chainId, name: 'Avalanche Fuji Testnet', isMainnet: false };
  return { chainId, name: `Chain ${chainId}`, isMainnet: false };
}

// ─── Status ───────────────────────────────────────────────────────────────────

export async function getTestWalletLockStatus(
  tokenAddress: string,
  walletAddress: string,
): Promise<TestWalletLockStatus> {
  const p = provider();
  const wallet = ethers.getAddress(walletAddress);
  const { locker } = await findLocker(tokenAddress, p);
  if (!locker)
    return {
      hasLocker: false,
      whitelisted: false,
      locked: '0',
      available: '0',
    };
  const decimals = Number(
    await new ethers.Contract(tokenAddress, TOKEN_ABI, p).decimals(),
  );
  const [whitelisted, locked, available] = await Promise.all([
    locker.isWhitelisted(wallet) as Promise<boolean>,
    locker.getLockedAmount(wallet) as Promise<bigint>,
    locker.getAvailableBalance(wallet) as Promise<bigint>,
  ]);
  return {
    hasLocker: true,
    whitelisted,
    locked: ethers.formatUnits(locked, decimals),
    available: ethers.formatUnits(available, decimals),
  };
}

// ─── Whitelist ────────────────────────────────────────────────────────────────

/**
 * Exempt `walletAddress` from the token lock for `tokenAddress`. Two routes, tried in order:
 *
 *   1. `locker.setWhitelistAddressByOwner` — a direct call by the module's owner. The mainnet
 *      AZX1 module has it (owner: the treasury); this is whitelistInvestorMainnet.ts.
 *   2. `setWhitelistAddress` via `compliance.callModuleFunction` — for modules without the
 *      owner entry point, e.g. Fuji's. Compliance is normally owned by the
 *      ERC1155AndTREXManager, whose `callComplianceModuleFunction` forwards it in one
 *      transaction; a Turnkey wallet owning compliance directly calls it itself.
 *
 * Route 1 is simulated before it is chosen, so a module without the function — or one owned
 * by a wallet we cannot sign for — falls through to route 2 instead of failing.
 */
export async function whitelistWallet(
  creds: AssetTransferCreds,
  tokenAddress: string,
  walletAddress: string,
): Promise<TestingResult> {
  const p = provider();
  const wallet = ethers.getAddress(walletAddress);
  const { compliance, locker } = await findLocker(tokenAddress, p);
  if (!locker)
    throw new Error(
      'This token has no TokenLockerModule — its tokens are never locked, so there is nothing to whitelist.',
    );
  if (await locker.isWhitelisted(wallet))
    throw new Error(`${wallet} is already whitelisted.`);

  const ours = candidates(creds);
  const tx =
    (await whitelistViaModuleOwner(creds, locker, wallet, ours, p)) ??
    (await whitelistViaCompliance(creds, compliance, locker, wallet, ours, p));

  const receipt = await tx.wait();
  if (!receipt) throw new Error('Transaction receipt is null.');
  if (!(await locker.isWhitelisted(wallet)))
    throw new Error(
      `Transaction ${receipt.hash} mined but ${wallet} is still not whitelisted.`,
    );
  return { txHash: receipt.hash };
}

/** Route 1: the module owner's direct entry point. Null when it does not apply here. */
async function whitelistViaModuleOwner(
  creds: AssetTransferCreds,
  locker: ethers.Contract,
  wallet: string,
  ours: string[],
  p: ethers.Provider,
): Promise<ethers.TransactionResponse | null> {
  let owner: string;
  try {
    owner = await locker.owner();
  } catch {
    return null; // module is not Ownable
  }
  const signerAddr = ours.find((a) => sameAddress(a, owner));
  if (!signerAddr) return null;
  const signed = new ethers.Contract(
    await locker.getAddress(),
    LOCKER_ABI,
    signerFor(creds, signerAddr, p),
  );
  try {
    await signed.setWhitelistAddressByOwner.staticCall(wallet, true);
  } catch {
    return null; // this module predates setWhitelistAddressByOwner
  }
  return signed.setWhitelistAddressByOwner(wallet, true);
}

/** Route 2: setWhitelistAddress through compliance — directly or via the Manager. */
async function whitelistViaCompliance(
  creds: AssetTransferCreds,
  compliance: string,
  locker: ethers.Contract,
  wallet: string,
  ours: string[],
  p: ethers.Provider,
): Promise<ethers.TransactionResponse> {
  const calldata = locker.interface.encodeFunctionData('setWhitelistAddress', [
    wallet,
    true,
  ]);
  const moduleAddress = await locker.getAddress();

  const complianceOwner: string = await new ethers.Contract(
    compliance,
    COMPLIANCE_ABI,
    p,
  ).owner();

  const directOwner = ours.find((a) => sameAddress(a, complianceOwner));
  if (directOwner) {
    const c = new ethers.Contract(
      compliance,
      COMPLIANCE_ABI,
      signerFor(creds, directOwner, p),
    );
    await c.callModuleFunction.staticCall(calldata, moduleAddress);
    return c.callModuleFunction(calldata, moduleAddress);
  } else {
    // Compliance owned by a contract — the Manager. Its owner must be one of our wallets.
    let managerOwner: string;
    try {
      managerOwner = await new ethers.Contract(
        complianceOwner,
        MANAGER_ABI,
        p,
      ).owner();
    } catch {
      throw new Error(
        `Compliance is owned by ${complianceOwner}, which is neither of your wallets nor a Manager contract.`,
      );
    }
    const signerAddr = ours.find((a) => sameAddress(a, managerOwner));
    if (!signerAddr)
      throw new Error(
        `Only the Manager owner ${managerOwner} can whitelist — it is neither the treasury nor the relayer wallet you unlocked with.`,
      );
    const manager = new ethers.Contract(
      complianceOwner,
      MANAGER_ABI,
      signerFor(creds, signerAddr, p),
    );
    // Simulate first: an older Manager implementation has no callComplianceModuleFunction,
    // and that should fail here, not as a mined revert that still costs gas.
    try {
      await manager.callComplianceModuleFunction.staticCall(
        compliance,
        calldata,
        moduleAddress,
      );
    } catch (err: any) {
      throw new Error(
        `Whitelist simulation failed — the Manager at ${complianceOwner} may not support callComplianceModuleFunction. ${err?.shortMessage ?? err?.message ?? ''}`,
      );
    }
    return manager.callComplianceModuleFunction(
      compliance,
      calldata,
      moduleAddress,
    );
  }
}

// ─── Revert ───────────────────────────────────────────────────────────────────

/**
 * Move every `tokenAddress` token held by `walletAddress` back to the treasury.
 *
 * Uses the agent-only `forcedTransfer`: the tester's wallet does not sign and needs no gas,
 * the ACE policy is not consulted (forcedTransfer carries no runPolicy), and frozen tokens are
 * unfrozen as needed. The destination is the treasury the lock module itself records; it must
 * match the treasury wallet the admin unlocked with, so the confirmation they clicked through
 * named the real destination.
 */
export async function revertWalletTokens(
  creds: AssetTransferCreds,
  tokenAddress: string,
  walletAddress: string,
): Promise<TestingResult> {
  const p = provider();
  const wallet = ethers.getAddress(walletAddress);
  const treasury = ethers.getAddress(creds.walletAddress);
  if (sameAddress(wallet, treasury))
    throw new Error('The test wallet is the treasury — nothing to revert.');

  const token = new ethers.Contract(tokenAddress, TOKEN_ABI, p);
  const [balance, decimalsRaw] = await Promise.all([
    token.balanceOf(wallet) as Promise<bigint>,
    token.decimals() as Promise<bigint>,
  ]);
  if (balance === BigInt(0))
    throw new Error(`${wallet} holds no tokens to revert.`);
  const decimals = Number(decimalsRaw);

  const { locker } = await findLocker(tokenAddress, p);
  if (locker) {
    const onChainTreasury: string = await locker.getTreasuryAddress();
    if (!sameAddress(onChainTreasury, treasury))
      throw new Error(
        `The lock module's treasury is ${onChainTreasury}, not the wallet you unlocked with (${treasury}). Unlock with the token's treasury wallet.`,
      );
    // A non-whitelisted sender can only move unlocked tokens; anything still locked makes
    // the module revert the whole transfer.
    const [whitelisted, available] = await Promise.all([
      locker.isWhitelisted(wallet) as Promise<boolean>,
      locker.getAvailableBalance(wallet) as Promise<bigint>,
    ]);
    if (!whitelisted && available < balance)
      throw new Error(
        `${ethers.formatUnits(balance - available, decimals)} of this wallet's tokens are still locked. Whitelist the wallet first, then revert.`,
      );
  }

  const ours = candidates(creds);
  let agent: string | undefined;
  for (const a of ours) {
    if (await token.isAgent(a)) {
      agent = a;
      break;
    }
  }
  if (!agent)
    throw new Error(
      'Neither the treasury nor the relayer wallet is an agent on this token — only an agent can revert.',
    );

  const signed = new ethers.Contract(
    tokenAddress,
    TOKEN_ABI,
    signerFor(creds, agent, p),
  );
  await signed.forcedTransfer.staticCall(wallet, treasury, balance);
  const tx = await signed.forcedTransfer(wallet, treasury, balance);
  const receipt = await tx.wait();
  if (!receipt) throw new Error('Transaction receipt is null.');

  return {
    txHash: receipt.hash,
    tokens: ethers.formatUnits(balance, decimals),
  };
}
